import React, { useState } from 'react';
import { X } from 'lucide-react';
import { motion } from 'framer-motion';
import axios from 'axios';

export default function EditTemplate({ template, onClose, isDarkMode, themeClasses, onTemplateUpdated }) {
  const [formData, setFormData] = useState({
    name: template.name,
    description: template.description || '',
    extractFields: template.fields || [],
    instructions: template.instructions || ''
  });
  const [newField, setNewField] = useState('');
  const [error, setError] = useState(null);

  const handleFieldKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      if (newField.trim()) {
        setFormData(prev => ({
          ...prev,
          extractFields: [...prev.extractFields, newField.trim()]
        }));
        setNewField('');
      }
    }
  };

  const removeField = (indexToRemove) => {
    setFormData(prev => ({
      ...prev,
      extractFields: prev.extractFields.filter((_, index) => index !== indexToRemove)
    }));
  };

  const handleSubmit = async () => {
    try {
      const token = localStorage.getItem('authToken');
      await axios.put(`/api/templates/${template.id}`, {
        name: formData.name,
        description: formData.description,
        fields: formData.extractFields,
        instructions: formData.instructions
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      onTemplateUpdated();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update template');
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className={`
        relative w-full max-w-2xl rounded-2xl shadow-2xl
        ${isDarkMode ? 'bg-gray-800' : 'bg-white'}
        p-6
        max-h-[90vh]
        overflow-hidden
        flex flex-col
      `}>
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className={`text-xl font-semibold ${themeClasses.text}`}>
            Template
          </h2>
          <button
            onClick={onClose}
            className={`p-2 rounded-lg transition-colors
              ${isDarkMode 
                ? 'hover:bg-gray-700 text-gray-400'
                : 'hover:bg-gray-100 text-gray-600'
              }`}
          >
            <X size={20} />
          </button>
        </div>

        {/* Content - Add overflow-y-auto to make content scrollable */}
        <div className="flex-1 overflow-y-auto">
          <div className="space-y-6">
            <div className="space-y-4">
              <div>
                <label className={`block text-sm font-medium ${themeClasses.text} mb-1`}>
                  Template Name
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className={`
                    w-full px-3 py-2 rounded-lg
                    ${isDarkMode
                      ? 'bg-gray-700 text-gray-200 border-gray-600'
                      : 'bg-white text-gray-900 border-gray-300'
                    }
                    border focus:ring-2 focus:ring-blue-500
                  `}
                />
              </div>

              <div>
                <label className={`block text-sm font-medium ${themeClasses.text} mb-1`}>
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  rows="3"
                  className={`
                    w-full px-3 py-2 rounded-lg
                    ${isDarkMode
                      ? 'bg-gray-700 text-gray-200 border-gray-600'
                      : 'bg-white text-gray-900 border-gray-300'
                    }
                    border focus:ring-2 focus:ring-blue-500
                  `}
                />
              </div>

              <div>
                <label className={`block text-sm font-medium ${themeClasses.text} mb-1`}>
                  Extract Fields
                </label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {formData.extractFields.map((field, index) => (
                    <span
                      key={index}
                      className={`
                        inline-flex items-center px-3 py-1 rounded-full text-sm
                        ${isDarkMode
                          ? 'bg-gray-700 text-gray-200'
                          : 'bg-gray-100 text-gray-800'
                        }
                      `}
                    >
                      {field}
                      <button
                        onClick={() => removeField(index)}
                        className="ml-2 text-gray-500 hover:text-gray-700"
                      >
                        <X size={14} />
                      </button>
                    </span>
                  ))}
                </div>
                <input
                  type="text"
                  value={newField}
                  onChange={e => setNewField(e.target.value)}
                  onKeyDown={handleFieldKeyDown}
                  placeholder="Type and press Enter or comma to add fields"
                  className={`
                    w-full px-3 py-2 rounded-lg
                    ${isDarkMode
                      ? 'bg-gray-700 text-gray-200 border-gray-600'
                      : 'bg-white text-gray-900 border-gray-300'
                    }
                    border focus:ring-2 focus:ring-blue-500
                  `}
                />
              </div>

              <div>
                <label className={`block text-sm font-medium ${themeClasses.text} mb-1`}>
                  Instructions
                </label>
                <textarea
                  value={formData.instructions}
                  onChange={e => setFormData(prev => ({ ...prev, instructions: e.target.value }))}
                  rows="4"
                  className={`
                    w-full px-3 py-2 rounded-lg
                    ${isDarkMode
                      ? 'bg-gray-700 text-gray-200 border-gray-600'
                      : 'bg-white text-gray-900 border-gray-300'
                    }
                    border focus:ring-2 focus:ring-blue-500
                  `}
                />
              </div>
            </div>

            {error && (
              <div className="p-4 rounded-lg bg-red-500/20 border border-red-500/30 text-red-400">
                {error}
              </div>
            )}

            <div className="flex justify-end space-x-4">
              <button
                onClick={onClose}
                className={`
                  px-4 py-2 rounded-lg
                  ${isDarkMode
                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }
                `}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                className="px-4 py-2 rounded-lg bg-blue-500 text-white hover:bg-blue-600"
              >
                Update Template
              </button>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
} 